import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faSpinner, faCheck, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import intruderService from '../../services/intruderServiceFrontend';

const IntruderResults = ({ attack, grepMatch, grepExtract, onViewResult, isDarkMode }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedResultId, setSelectedResultId] = useState(null);
  const pollRef = useRef(null);

  const loadResults = async () => {
    if (!attack?._id) return;
    try {
      setLoading(true);
      const data = await intruderService.getResults(attack._id);
      setResults(data.results || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
  }, [attack?._id]);

  // Poll while running
  useEffect(() => {
    if (attack?.status === 'running') {
      pollRef.current = setInterval(() => {
        intruderService.getResults(attack._id).then(data => {
          setResults(data.results || []);
        }).catch(() => {});
      }, 2000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [attack?.status, attack?._id]);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortField(null); setSortDir('asc'); }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    if (!sortField) return 0;
    let aVal, bVal;
    switch (sortField) {
      case 'payload': aVal = a.mutation?.mutatedValue || ''; bVal = b.mutation?.mutatedValue || ''; break;
      case 'status': aVal = a.response?.statusCode || 0; bVal = b.response?.statusCode || 0; break;
      case 'length': aVal = a.response?.contentLength || 0; bVal = b.response?.contentLength || 0; break;
      case 'time': aVal = a.response?.timing?.totalMs || 0; bVal = b.response?.timing?.totalMs || 0; break;
      default: aVal = 0; bVal = 0;
    }
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const sortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  const statusColor = (code) => {
    if (!code) return isDarkMode ? 'text-gray-500' : 'text-gray-400';
    if (code < 300) return 'text-green-500';
    if (code < 400) return 'text-blue-400';
    if (code < 500) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Compute baseline for anomaly detection
  const baselineStatus = results.length > 0 ? results[0]?.response?.statusCode : null;
  const baselineLength = results.length > 0 ? results[0]?.response?.contentLength : null;

  const isAnomaly = (result) => {
    const status = result.response?.statusCode;
    const length = result.response?.contentLength;
    if (status !== baselineStatus) return true;
    if (baselineLength && length && Math.abs(length - baselineLength) > baselineLength * 0.1) return true;
    return false;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Status bar */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {results.length} results
          </span>
          {attack?.status === 'running' && (
            <span className="flex items-center gap-1 text-[10px] text-blue-400">
              <FontAwesomeIcon icon={faSpinner} spin />
              Running... {attack.progress?.completedRequests || 0}/{attack.progress?.totalRequests || 0}
            </span>
          )}
          {attack?.status === 'completed' && (
            <span className="flex items-center gap-1 text-[10px] text-green-400">
              <FontAwesomeIcon icon={faCheck} />
              Completed
            </span>
          )}
          {attack?.status === 'failed' && (
            <span className="flex items-center gap-1 text-[10px] text-red-400">
              <FontAwesomeIcon icon={faTimes} />
              Failed
            </span>
          )}
        </div>
      </div>

      {/* Results table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              <th className="text-left px-2 py-1.5 w-12">#</th>
              <th className="text-left px-2 py-1.5 cursor-pointer select-none" onClick={() => handleSort('payload')}>
                <span className="flex items-center gap-1">Payload <FontAwesomeIcon icon={sortIcon('payload')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-16 cursor-pointer select-none" onClick={() => handleSort('status')}>
                <span className="flex items-center gap-1">Status <FontAwesomeIcon icon={sortIcon('status')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-20 cursor-pointer select-none" onClick={() => handleSort('length')}>
                <span className="flex items-center gap-1">Length <FontAwesomeIcon icon={sortIcon('length')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-16 cursor-pointer select-none" onClick={() => handleSort('time')}>
                <span className="flex items-center gap-1">Time <FontAwesomeIcon icon={sortIcon('time')} className="text-[8px] opacity-60" /></span>
              </th>
              {/* Dynamic grep match columns */}
              {(grepMatch || []).map((rule, i) => (
                <th key={`gm-${i}`} className="text-left px-2 py-1.5 w-16">
                  <span className="text-[10px]">{rule.name || `Match ${i + 1}`}</span>
                </th>
              ))}
              {/* Dynamic grep extract columns */}
              {(grepExtract || []).map((rule, i) => (
                <th key={`ge-${i}`} className="text-left px-2 py-1.5">
                  <span className="text-[10px]">{rule.name || `Extract ${i + 1}`}</span>
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((r, i) => {
              const anomaly = isAnomaly(r);
              return (
                <tr
                  key={r._id || i}
                  onClick={() => { setSelectedResultId(r._id); onViewResult?.(r); }}
                  className={`cursor-pointer border-b ${
                    selectedResultId === r._id
                      ? (isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50')
                      : anomaly
                        ? (isDarkMode ? 'bg-yellow-900/10 border-gray-800 hover:bg-yellow-900/20' : 'bg-yellow-50 border-gray-100 hover:bg-yellow-100')
                        : (isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50')
                  }`}
                >
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{i + 1}</td>
                  <td className={`px-2 py-1 font-mono truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {r.mutation?.mutatedValue || '-'}
                  </td>
                  <td className="px-2 py-1">
                    <span className={`font-mono font-bold ${statusColor(r.response?.statusCode)}`}>
                      {r.response?.statusCode || '-'}
                    </span>
                  </td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {r.response?.contentLength != null ? r.response.contentLength : '-'}
                  </td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {r.response?.timing?.totalMs != null ? `${r.response.timing.totalMs}ms` : '-'}
                  </td>
                  {/* Grep match values */}
                  {(grepMatch || []).map((rule, gi) => {
                    const matched = r.grepResults?.matches?.[gi]?.matched;
                    return (
                      <td key={`gm-${gi}`} className="px-2 py-1 text-center">
                        {matched != null ? (
                          matched ? (
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-[10px]" />
                          ) : (
                            <FontAwesomeIcon icon={faTimes} className="text-gray-600 text-[10px]" />
                          )
                        ) : '-'}
                      </td>
                    );
                  })}
                  {/* Grep extract values */}
                  {(grepExtract || []).map((rule, gi) => (
                    <td key={`ge-${gi}`} className={`px-2 py-1 font-mono text-[10px] truncate max-w-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {r.grepResults?.extracts?.[gi]?.value || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewResult?.(r); }}
                      className={`p-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                      title="View details"
                    >
                      <FontAwesomeIcon icon={faEye} className="text-[10px]" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {results.length === 0 && !loading && (
          <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {attack?.status === 'configured' ? 'Start the attack to see results.' : 'No results yet.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntruderResults;
